import type { AccountCollection, AccountPage } from "./account-contract";

export class AccountClientError extends Error {
  constructor(public readonly status: number) { super(status === 401 ? "Inicia sesión para guardar tu investigación."
    : status === 403 ? "Verifica tu correo antes de continuar." : status === 404 ? "Esta opción ya no está disponible en el catálogo."
      : "No pudimos conectar con tu cuenta. Inténtalo de nuevo."); }
}
/** Same-origin only, no browser identity/tenant arguments or admin headers. */
export async function accountRequest(collection: AccountCollection, method: "GET" | "PUT" | "DELETE", body?: unknown, after?: string, signal?: AbortSignal) {
  if (!["favorites", "comparisons", "preferences"].includes(collection) || after && !/^[A-Za-z0-9_-]{1,160}$/.test(after)) throw new AccountClientError(400);
  const response = await fetch(`/api/account/${collection}${after ? `?after=${encodeURIComponent(after)}` : ""}`, { method,
    credentials: "same-origin", cache: "no-store", redirect: "error", signal,
    ...(body !== undefined ? { headers: { "content-type": "application/json" }, body: JSON.stringify(body) } : {}) });
  if (!response.ok) throw new AccountClientError(response.status);
  return response.json();
}
export async function notificationRequest(method: "POST" | "DELETE", body: unknown, signal?: AbortSignal) {
  const response = await fetch("/api/account/notifications", { method, credentials: "same-origin", cache: "no-store", redirect: "error", signal,
    headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  if (!response.ok) throw new AccountClientError(response.status);
  return response.json();
}
export async function loadAccountPage(collection: AccountCollection, after?: string, signal?: AbortSignal): Promise<AccountPage> {
  const result = await accountRequest(collection, "GET", undefined, after, signal);
  if (!Array.isArray(result?.records) || result.records.length > 25 || !(result.nextCursor === null || typeof result.nextCursor === "string")) throw new AccountClientError(503);
  return result as AccountPage;
}

/** Download stays in browser memory; no Storage upload or emailed attachment. */
export async function loadAccountExport(signal?: AbortSignal): Promise<Blob> {
  const response = await fetch("/api/account/export", { method: "POST", credentials: "same-origin", cache: "no-store", redirect: "error", signal,
    headers: { "content-type": "application/json" }, body: "{}" });
  if (!response.ok) throw new AccountClientError(response.status);
  if (!response.body || !/^application\/json(?:\s*;|$)/i.test(response.headers.get("content-type") ?? "")) throw new AccountClientError(503);
  const reader = response.body.getReader(), chunks: Uint8Array<ArrayBuffer>[] = []; let bytes = 0;
  try {
    for (;;) { const part = await reader.read(); if (part.done) break; bytes += part.value.byteLength;
      if (bytes > 2 * 1024 * 1024) throw new AccountClientError(503); chunks.push(new Uint8Array(part.value)); }
  } finally { await reader.cancel().catch(() => undefined); }
  signal?.throwIfAborted();
  const blob = new Blob(chunks, { type: "application/json" });
  let value;
  try { value = JSON.parse(await blob.text()); } catch { throw new AccountClientError(503); }
  if (value?.schemaVersion !== 1 || value?.scope !== "codifly_private_library" || !value?.collections) throw new AccountClientError(503);
  return blob;
}
